import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';

import { Store } from '@ngrx/store';

import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DomSanitizer } from '@angular/platform-browser';

import { AppState, Selectors } from 'store/root-reducer';
import { Document } from 'modules/shared/models/document.models';
import { CreateOrUpdateRecordDocumentRequest, DocumentCreateOrUpdateEvent, DocumentThumbnail } from 'store/models/document.models';
import { BillableItem } from 'modules/shared/models/billable.models';

import { DocumentsService } from 'modules/core/services/documents.service';
import { SnackbarService } from 'app/services/snackbar.service';

import { MAX_UPLOAD_SIZE } from 'modules/shared/constants/upload.constants';

interface DisplayDocument {
  thumbnail?: string;
  backThumbnail?: string;
  isFrontPdfFile: boolean;
  isBackPdfFile: boolean;
}

@Component({
  selector: 'artemis-document-update',
  templateUrl: './document-update.component.html',
  styleUrls: ['./document-update.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class DocumentUpdateComponent implements OnInit, OnDestroy {

  @Input() documentTypes: Array<string> = [];
  @Input() authenticities: Array<string> = [];

  @Input() set uploadedDocument(document: Document) {
    if (!document) {
      return;
    }
    if (document.document && document.backDocument) {
      this.displayDocument = {
        isFrontPdfFile: document.document.includes('.pdf'),
        isBackPdfFile: document.backDocument.includes('.pdf'),
        thumbnail: this.sanitizer.bypassSecurityTrustResourceUrl(document.document) as string,
        backThumbnail: this.sanitizer.bypassSecurityTrustResourceUrl(document.backDocument) as string,
      };
    } else if (document.document) {
      this.displayDocument = {
        isFrontPdfFile: document.document.includes('.pdf'),
        isBackPdfFile: false,
        thumbnail: this.sanitizer.bypassSecurityTrustResourceUrl(document.document) as string,
        backThumbnail: null,
      };
    } else if (document.backDocument) {
      this.displayDocument = {
        isFrontPdfFile: null,
        isBackPdfFile: document.backDocument.includes('.pdf'),
        thumbnail: null,
        backThumbnail: this.sanitizer.bypassSecurityTrustResourceUrl(document.backDocument) as string,
      };
    } else {
      this.displayDocument = {
        isFrontPdfFile: false,
        isBackPdfFile: false,
        thumbnail: null,
        backThumbnail: null,
      };
    }
    this.updatedDocument = { ...document };
    if (this.documentInfoForm) {
      this.documentInfoForm.reset();
      this.documentInfoForm.markAsPristine();
    }
    this.documentInfoForm.patchValue(this.updatedDocument);
    this.checkOtherDocumentTypeValue();
    this.cdRef.detectChanges();
  }
  @Output() updatedDocumentOutput: EventEmitter<DocumentCreateOrUpdateEvent> = new EventEmitter();

  readonly allowedFileType: Array<string> = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

  private unsubscribe$: Subject<void> = new Subject();

  uploading: boolean = false;

  documentInfoForm: FormGroup;

  updatedDocument: CreateOrUpdateRecordDocumentRequest = <CreateOrUpdateRecordDocumentRequest>{};
  displayDocument: DisplayDocument;

  isOtherDocType: boolean;

  maxIssueDate: Date = new Date();
  minExpiryDate: Date;

  fcOrOcrEnabled: boolean;

  documentThumbnail: DocumentThumbnail[] = [];

  constructor(private fb: FormBuilder,
    private documentService: DocumentsService,
    private snackBar: SnackbarService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private datePipe: DatePipe) {
    this.buildDocumentForm();
  }

  ngOnInit() {
    this.getBillableInfo();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Builds form for the document
   * @method buildDocumentForm
   */
  private buildDocumentForm(): void {
    this.documentInfoForm = this.fb.group({
      docType: [this.updatedDocument.docType || '', Validators.required],
      docTypeCategory: [this.updatedDocument.docTypeCategory || ''],
      otherDocType: [''],
      authenticity: [this.updatedDocument.authenticity || ''],
      docName: [{ value: this.updatedDocument.docName || '', disabled: true }, Validators.required],
      expiryDate: [this.updatedDocument.expiryDate || ''],
      issueDate: [this.updatedDocument.issueDate || ''],
      showExpiryAlerts: [true, this.updatedDocument.showExpiryAlerts || false],
      document: [this.updatedDocument.document],
      documentNumber: [this.updatedDocument.documentNumber || ''],
      backDocument: [this.updatedDocument.backDocument],
      backDocName: [{ value: this.updatedDocument.backDocName || '', disabled: true }, Validators.required],
    });

    if (this.documentInfoForm.get('docName')) {
      this.documentInfoForm
        .get('docName')
        .valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (value: string) => {
            if (value) {
              const index = this.documentThumbnail.findIndex(e => e.type === 'docName');
              if (index > -1) {
                this.documentThumbnail.splice(index, 1, {
                  type: 'docName',
                  value: value,
                });
              } else {
                this.documentThumbnail.splice(0, 0, {
                  type: 'docName',
                  value: value,
                });
              }
            }
          }
        );
    }

    if (this.documentInfoForm.get('backDocName')) {
      this.documentInfoForm
        .get('backDocName')
        .valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (value: string) => {
            if (value) {
              const index = this.documentThumbnail.findIndex(e => e.type === 'backDocName');
              if (index > -1) {
                this.documentThumbnail.splice(index, 1, {
                  type: 'backDocName',
                  value: value,
                });
              } else {
                this.documentThumbnail.splice(0, 0, {
                  type: 'backDocName',
                  value: value,
                });
              }
            }
          }
        );
    }

    if (this.isOtherDocType) {
      this.documentInfoForm.get('otherDocType').setValidators(Validators.required);
    } else {
      this.documentInfoForm.get('otherDocType').clearValidators();
    }

    this.documentInfoForm.get('otherDocType').updateValueAndValidity();

    this.documentInfoForm.get('issueDate').valueChanges.subscribe((issueDate: Date) => {
      if (issueDate) {
        this.minExpiryDate = new Date(new Date(issueDate).getTime() + 1000 * 60 * 60 * 24);
      }
    });

    this.checkOtherDocumentTypeValue();
  }

  /**
   * @method checkOtherDocumentTypeValue
   * Check if the document type value is customized and set accordingly
   */
  private checkOtherDocumentTypeValue(): void {
    let allDocumentTypeValues: Array<string> = [];

    if (this.documentTypes) {
      this.documentTypes.forEach((documentTypeCategory: any) => {
        allDocumentTypeValues = allDocumentTypeValues.concat(documentTypeCategory.documentTypes);
      });
    }

    const isOtherDocumentType: boolean = this.updatedDocument.docType &&
      (this.updatedDocument.docType.toLowerCase() === 'others (pls specify)' ||
        !~allDocumentTypeValues.indexOf(this.updatedDocument.docType));

    if (isOtherDocumentType) {
      this.isOtherDocType = true;

      if (this.updatedDocument.docTypeCategory && this.updatedDocument.docTypeCategory.toLowerCase() !== 'others (pls specify)') {
        this.documentInfoForm.patchValue({
          'docType': `OTHER ${this.updatedDocument.docTypeCategory}`,
          'otherDocType': this.updatedDocument.docType
        });
      } else {
        this.documentInfoForm.patchValue({
          'docType': `OTHERS (PLS SPECIFY)`,
          'otherDocType': this.updatedDocument.docType
        });
      }
    } else {
      this.isOtherDocType = false;
    }
  }

  /**
   * Get subscription information from state to decide whether FC and OCR are enabled
   * @method getBillableInfo
   */
  private getBillableInfo(): void {
    combineLatest([
      this.store.select(Selectors.billable.getOCR),
      this.store.select(Selectors.billable.getFC)
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([ocrEnabled, fcEnabled]: [BillableItem, BillableItem]) => {
        if (ocrEnabled && fcEnabled && (ocrEnabled.display || fcEnabled.display)) {
          this.fcOrOcrEnabled = true;
          this.documentInfoForm.get('document').setValidators([Validators.required]);
          this.documentInfoForm.get('document').updateValueAndValidity();
        }
      });
  }

  /**
   * Builds form for the document
   * @method updateDocument
   */
  updateDocument(): void {
    if (this.documentInfoForm.valid) {
      this.uploading = true;

      if (this.isOtherDocType) {
        this.documentInfoForm.get('docType').setValue(this.documentInfoForm.get('otherDocType').value);
      }

      Object.assign(this.updatedDocument, {
        ...this.documentInfoForm.value,
        issueDate: this.datePipe.transform(this.documentInfoForm.get('issueDate').value, 'yyyy-MM-dd'),
        expiryDate: this.datePipe.transform(this.documentInfoForm.get('expiryDate').value, 'yyyy-MM-dd')
      });

      if (typeof this.updatedDocument.document === 'string') {
        delete this.updatedDocument.document;
      }

      if (typeof this.updatedDocument.backDocument === 'string') {
        delete this.updatedDocument.backDocument;
      }

      if (this.updatedDocument.id) {
        this.documentService.updateRecordDocument(this.updatedDocument).subscribe(document => {
          this.updatedDocumentOutput.next({ document, mode: 'UPDATE' });
          this.uploading = false;
        }, error => {
          if (error.error.detail === 'File has invalid content-type. Maybe file extension is not equal to file content?') {
            this.uploading = false;
            this.updatedDocumentOutput.next({ document: null, mode: 'CREATE', error: error.error.detail });
          } else {
            this.uploading = false;
            this.updatedDocumentOutput.next({ document: null, mode: 'CREATE', error: error.message });
          }
        });
      } else {
        this.documentService.createRecordDocument(this.updatedDocument).subscribe(document => {
          this.uploading = false;
          this.updatedDocumentOutput.next({ document, mode: 'CREATE' });
        }, error => {
          if (error.error.detail === 'File has invalid content-type. Maybe file extension is not equal to file content?') {
            this.uploading = false;
            this.updatedDocumentOutput.next({ document: null, mode: 'CREATE', error: error.error.detail });
          } else {
            this.uploading = false;
            this.updatedDocumentOutput.next({ document: null, mode: 'CREATE', error: error.message });
          }
        });
      }
    }
  }

  /**
   * Handler when front of document is uploaded
   * @method frontDocumentUploadedHandler
   */
  frontDocumentUploadedHandler(files: FileList): void {
    if (!files || files.length === 0) {
      return;
    }
    if (files[0].size > 10 * MAX_UPLOAD_SIZE) {
      this.snackBar.error(`File size exceeds maximum limit 10 MB.`);
      return;
    }

    if (!this.allowedFileType.includes(files[0].type)) {
      this.snackBar.error(`Only png, jpg, jpeg or pdf is allowed.`);
      return;
    }

    if (files[0].name.includes('.pdf')) {
      this.displayDocument.isFrontPdfFile = true;
    } else {
      this.displayDocument.isFrontPdfFile = false;
    }
    const fileReader: FileReader = new FileReader();
    this.documentInfoForm.get('docName').setValue(files[0].name);
    this.documentInfoForm.get('document').setValue(files[0]);
    this.updatedDocument.frontDocumentFile = files[0];
    if (this.displayDocument.thumbnail) {
      this.displayDocument.thumbnail = null;
    }
    this.documentInfoForm.markAsDirty();
    this.cdRef.detectChanges();

    fileReader.onload = (event: ProgressEvent) => {
      this.displayDocument.thumbnail = this.sanitizer.bypassSecurityTrustResourceUrl((<any>event.target).result) as string;
      this.cdRef.detectChanges();
    };

    fileReader.readAsDataURL(files[0]);
  }

  /**
     * Handler when back of document is uploaded
     * @method documentUploadedHandler
     */
  backDocumentUploadedHandler(files: FileList): void {
    if (!files || files.length === 0) {
      return;
    }
    if (files[0].size > 10 * MAX_UPLOAD_SIZE) {
      this.snackBar.error(`File size exceeds maximum limit 10 MB.`);
      return;
    }

    if (!this.allowedFileType.includes(files[0].type)) {
      this.snackBar.error(`Only png, jpg, jpeg or pdf is allowed.`);
      return;
    }

    if (files[0].name.includes('.pdf')) {
      this.displayDocument.isBackPdfFile = true;
    } else {
      this.displayDocument.isBackPdfFile = false;
    }
    const fileReader: FileReader = new FileReader();
    this.documentInfoForm.get('backDocName').setValue(files[0].name);
    this.documentInfoForm.get('backDocument').setValue(files[0]);
    this.updatedDocument.backDocumentFile = files[0];
    if (this.displayDocument.backThumbnail) {
      this.displayDocument.backThumbnail = null;
    }
    this.documentInfoForm.markAsDirty();
    this.cdRef.detectChanges();

    fileReader.onload = (event: ProgressEvent) => {
      this.displayDocument.backThumbnail = this.sanitizer.bypassSecurityTrustResourceUrl((<any>event.target).result) as string;
      this.cdRef.detectChanges();
    };

    fileReader.readAsDataURL(files[0]);
  }

  removeBackDocument(): void {
    this.documentInfoForm.get('backDocName').reset();
    this.documentInfoForm.get('backDocName').updateValueAndValidity();
    this.documentInfoForm.get('backDocument').reset();
    this.documentInfoForm.get('backDocument').updateValueAndValidity();
    this.displayDocument.backThumbnail = null;
    this.updatedDocument.backDocumentFile = null;
    this.updatedDocument.backDocName = null;
    this.documentInfoForm.markAsDirty();
  }

  /**
   * Handler when document is uploaded
   * @method getThumbnail
   */
  getThumbnail(): string {
    return this.displayDocument.thumbnail || this.updatedDocument.document;
  }

  /**
   * Handler when back document is uploaded
   * @method getBackThumbnail
   */
  getBackThumbnail(): string {
    return this.displayDocument.backThumbnail || this.updatedDocument.backDocument;
  }

  /**
   * @method setSelectedDocumentType
   * Set selected document type value
   */
  setSelectedDocumentType(documentTypeCategory: string, documentType: string): void {
    this.documentInfoForm.get('docTypeCategory').setValue(documentTypeCategory);
    this.isOtherDocType = documentType.toLowerCase() === 'others (pls specify)';

    if (documentType.toLowerCase() === 'others (pls specify)' && documentTypeCategory.toLowerCase() !== 'others (pls specify)') {
      this.documentInfoForm.get('docType').setValue(`OTHER ${documentTypeCategory}`);
    } else {
      this.documentInfoForm.get('docType').setValue(documentType);
    }

    this.documentInfoForm.markAsDirty();
  }
}
